import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';

/**
 * Selection metadata for the html-design skills — the category grouping, the one-clause
 * index summary, and the content tags that organize the "## Design & motion skills" index so the agent can
 * match a moment's content (a stat, a list, a scene change) to the right technique instead of
 * scanning a flat list of names.
 *
 * Kept beside the registry rather than as fields on each Skill so the definition files stay pure
 * technique content (markup + timeline code + pitfalls) and the shared Skill type stays unpolluted. A
 * registry test asserts this map covers every registered skill, so the two can't drift.
 *
 * A deliberate sibling of codeo's skillMeta: the two agents author against different composition
 * contracts and word their prompts differently, so the scaffolding is duplicated rather than shared.
 */

export type HtmlDesignSkillCategory =
    | 'drawing'
    | 'type'
    | 'emphasis'
    | 'text-effects'
    | 'data'
    | 'svg'
    | 'layout'
    | 'overlays'
    | 'annotation'
    | 'ambient'
    | 'texture'
    | 'camera'
    | 'transitions'
    | 'handoff'
    | 'ui'
    | 'craft';

/**
 * Render order + display label for each category in the system-prompt index.
 *
 * Kept finer-grained than feels necessary for the library's size: the index is scanned in full on
 * every design turn, so a category that grows past roughly a dozen entries stops narrowing anything
 * and should be split rather than left to sprawl.
 */
export const HTML_DESIGN_SKILL_CATEGORIES: { id: HtmlDesignSkillCategory; label: string }[] = [
    { id: 'drawing', label: 'Drawing objects & symbols' },
    { id: 'type', label: 'Type & headlines' },
    { id: 'emphasis', label: 'Word-level emphasis' },
    { id: 'text-effects', label: 'Text effects' },
    { id: 'data', label: 'Numbers & data' },
    { id: 'svg', label: 'Animating icons & line art' },
    { id: 'layout', label: 'Layout & comparison' },
    { id: 'overlays', label: 'Overlays & lower-thirds' },
    { id: 'annotation', label: 'Annotation & hand-drawn' },
    { id: 'ambient', label: 'Ambient & depth' },
    { id: 'texture', label: 'Surface & texture' },
    { id: 'camera', label: 'Camera moves' },
    { id: 'transitions', label: 'Scene transitions' },
    { id: 'handoff', label: 'Match cuts & handoffs' },
    { id: 'ui', label: 'UI & device motion' },
    { id: 'craft', label: 'Polish & finishing' },
];

export interface HtmlDesignSkillMeta {
    category: HtmlDesignSkillCategory;
    /**
     * The index line's wording — one clause naming the technique, not the paragraph a reader needs.
     * `Skill.description` stays the fuller human-facing text (gallery, tooling); this is what the
     * agent scans, and at library scale the difference is most of the section's token weight.
     */
    indexSummary: string;
    // Content/trigger keywords — what a moment is "about" when this technique fits.
    tags: string[];
}

/**
 * Techniques that read as a plain entrance: type that rises, fades or slides, a cascade, a bar that
 * grows, a background that drifts. Right for plenty of frames, but also what gets chosen by default
 * — so the chooser sorts them last and is told to prefer something with more in it.
 */
export const PLAIN_TECHNIQUES: ReadonlySet<string> = new Set([
    SKILL_NAMES.kineticWordReveal,
    SKILL_NAMES.letterSpacingSettle,
    SKILL_NAMES.softFocusResolve,
    SKILL_NAMES.headlineSlam,
    SKILL_NAMES.staggeredListReveal,
    SKILL_NAMES.cardGridAssemble,
    SKILL_NAMES.counterTickUp,
    SKILL_NAMES.statBarsAndFills,
    SKILL_NAMES.momentSweepTransition,
    SKILL_NAMES.sharedAxisSlide,
    SKILL_NAMES.cinematicCameraMove,
    SKILL_NAMES.driftAndHold,
    SKILL_NAMES.ambientFloatLoop,
    SKILL_NAMES.parallaxDepthDrift,
]);

// Keyed by Skill.name. A test in skillRegistry.spec enforces full coverage (every registered skill
// has an entry) and no orphans (no entry without a registered skill).
export const HTML_DESIGN_SKILL_META: Record<string, HtmlDesignSkillMeta> = {
    [SKILL_NAMES.objectConstructionMethod]: {
        category: 'drawing',
        indexSummary:
            'how to draw an object: silhouette, canvas, proportion, and what marks are made of',
        tags: [
            'draw',
            'object',
            'icon',
            'symbol',
            'illustration',
            'recognisable',
            'no-icon-library',
            'plant',
            'car',
            'person',
            'clock',
            'shield',
            'server',
            'sun',
            'building',
            'hand',
            'calendar',
            'grid',
            'keyline',
            'proportion',
            'stroke',
            'weight',
            'outline',
            'filled',
        ],
    },
    [SKILL_NAMES.iconSetConsistency]: {
        category: 'drawing',
        indexSummary: 'making several drawn objects in one graphic read as a family',
        tags: ['draw', 'set', 'family', 'consistency', 'density', 'spacing', 'several', 'three'],
    },
    [SKILL_NAMES.knockoutSilhouetteIcon]: {
        category: 'drawing',
        indexSummary: 'a drawn object rendered as a solid glyph with its detail cut out',
        tags: ['draw', 'glyph', 'silhouette', 'knockout', 'solid', 'mask', 'badge'],
    },
    [SKILL_NAMES.radialTickSystem]: {
        category: 'drawing',
        indexSummary: 'a radial dial, gauge, or sunburst built from generated ticks',
        tags: [
            'draw',
            'radial',
            'dial',
            'gauge',
            'speedometer',
            'compass',
            'sunburst',
            'spiral',
            'geometry',
            'motif',
        ],
    },
    [SKILL_NAMES.kineticWordReveal]: {
        category: 'type',
        indexSummary: 'headline arriving word by word, timed to the read',
        tags: ['headline', 'title', 'text-reveal', 'kinetic-type'],
    },
    [SKILL_NAMES.staggeredListReveal]: {
        category: 'type',
        indexSummary: 'list or steps cascading in one item at a time',
        tags: ['list', 'steps', 'bullets', 'cascade', 'sequence'],
    },
    [SKILL_NAMES.letterSpacingSettle]: {
        category: 'type',
        indexSummary: 'a short line closing from wide tracking as it fades up',
        tags: ['title', 'tracking', 'letter-spacing', 'premium', 'restraint', 'sign-off'],
    },
    [SKILL_NAMES.softFocusResolve]: {
        category: 'type',
        indexSummary: 'an element resolving out of blur, like a lens racking onto it',
        tags: ['blur', 'focus', 'rack-focus', 'lens', 'attention', 'depth', 'handoff'],
    },
    [SKILL_NAMES.headlineSlam]: {
        category: 'type',
        indexSummary: 'a short line landing hard with the frame reacting',
        tags: ['impact', 'slam', 'verdict', 'punchline', 'declaration', 'headline'],
    },
    [SKILL_NAMES.emphasisWordPulse]: {
        category: 'emphasis',
        indexSummary: 'one key word snapping to the accent color as it is spoken',
        tags: ['emphasis', 'keyword', 'accent', 'voice-sync'],
    },
    [SKILL_NAMES.markerEmphasis]: {
        category: 'emphasis',
        indexSummary: 'hand-drawn highlight or underline struck across a phrase',
        tags: ['highlight', 'underline', 'marker', 'emphasis'],
    },
    [SKILL_NAMES.strikethroughReplace]: {
        category: 'emphasis',
        indexSummary: 'a word struck out and replaced in place',
        tags: ['correction', 'replace', 'strikethrough', 'before-after', 'price-drop', 'not-this'],
    },
    [SKILL_NAMES.inlineHighlightChip]: {
        category: 'emphasis',
        indexSummary: 'a solid block wiping behind a phrase, knocking the text out',
        tags: ['highlight', 'chip', 'knockout', 'emphasis', 'typographic', 'claim'],
    },
    [SKILL_NAMES.typeAsObject]: {
        category: 'text-effects',
        indexSummary: 'the word itself becoming the thing it names',
        tags: ['headline', 'wordmark', 'lettering', 'arc-type', 'letter-swap', 'type-as-shape'],
    },
    [SKILL_NAMES.scrambleDecodeReveal]: {
        category: 'text-effects',
        indexSummary: 'a word resolving out of cycling glyphs, locking left to right',
        tags: ['decode', 'scramble', 'glyphs', 'resolve', 'computed', 'terminal', 'name'],
    },
    [SKILL_NAMES.typewriterWithCaret]: {
        category: 'text-effects',
        indexSummary: 'a line typed out character by character behind a caret',
        tags: ['typewriter', 'typing', 'caret', 'prompt', 'query', 'message', 'live'],
    },
    [SKILL_NAMES.glitchSliceText]: {
        category: 'text-effects',
        indexSummary: 'a word tearing into offset colour slices, then snapping clean',
        tags: ['glitch', 'disruption', 'unstable', 'break', 'warning', 'rgb-split', 'tear'],
    },
    [SKILL_NAMES.wordSwapInPlace]: {
        category: 'text-effects',
        indexSummary: 'one word cycling through a slot while the sentence stays put',
        tags: ['swap', 'variants', 'cycle', 'widening', 'built-for', 'slot', 'claim'],
    },
    [SKILL_NAMES.outlineToFillType]: {
        category: 'text-effects',
        indexSummary: 'letters drawn as outlines and then flooded with fill',
        tags: ['wordmark', 'outline', 'draw-on', 'fill', 'title', 'svg-text', 'brand'],
    },
    [SKILL_NAMES.splitFlapBoard]: {
        category: 'text-effects',
        indexSummary: 'a word flipping into place on split-flap panels',
        tags: ['split-flap', 'departure-board', 'flip', 'mechanical', 'destination', 'status'],
    },
    [SKILL_NAMES.textureMaskedType]: {
        category: 'text-effects',
        indexSummary: 'type cut out of a moving texture, so the word carries material',
        tags: ['mask', 'texture', 'material', 'title', 'knockout', 'gradient', 'wordmark'],
    },
    [SKILL_NAMES.streamingTextResponse]: {
        category: 'text-effects',
        indexSummary: 'text arriving word by word with a soft leading edge, as a model writes',
        tags: ['streaming', 'generated', 'ai', 'response', 'summary', 'draft', 'blur'],
    },
    [SKILL_NAMES.counterTickUp]: {
        category: 'data',
        indexSummary: 'a number ticking up and landing on its spoken value',
        tags: ['number', 'stat', 'count-up', 'metric'],
    },
    [SKILL_NAMES.statBarsAndFills]: {
        category: 'data',
        indexSummary: 'bars and progress fills growing to their values',
        tags: ['stat', 'bar-chart', 'comparison', 'progress'],
    },
    [SKILL_NAMES.dataAsMetaphor]: {
        category: 'data',
        indexSummary: 'a figure encoded as countable objects rather than a chart',
        tags: ['stat', 'chart', 'ranking', 'proportion', 'encoding', 'infographic', 'data'],
    },
    [SKILL_NAMES.numberWheelRoll]: {
        category: 'data',
        indexSummary: 'digits rolling into place on wheels, like an odometer',
        tags: ['number', 'odometer', 'wheel', 'tally', 'total', 'mechanical', 'counter'],
    },
    [SKILL_NAMES.chartStoryBuild]: {
        category: 'data',
        indexSummary: 'a chart assembling axis, then trend, then the point that matters',
        tags: ['chart', 'line', 'trend', 'series', 'growth', 'decline', 'data', 'crossover'],
    },
    [SKILL_NAMES.timelineRailAdvance]: {
        category: 'data',
        indexSummary: 'dated events advancing along a rail, one station at a time',
        tags: [
            'timeline',
            'chronology',
            'history',
            'milestones',
            'roadmap',
            'dates',
            'sequence',
            'evolution',
        ],
    },
    [SKILL_NAMES.ratingAndProgressFill]: {
        category: 'data',
        indexSummary: 'a rating row or progress ring filling to its value',
        tags: ['rating', 'stars', 'score', 'progress', 'ring', 'percentage', 'share', 'review'],
    },
    [SKILL_NAMES.svgDrawOn]: {
        category: 'svg',
        indexSummary: 'line art drawing itself on stroke by stroke',
        tags: ['svg', 'line-art', 'draw-on', 'diagram'],
    },
    [SKILL_NAMES.svgIconEnrichment]: {
        category: 'svg',
        indexSummary: 'an icon leading a benefit line, animated in parts',
        tags: ['icon', 'svg', 'symbol', 'accent'],
    },
    [SKILL_NAMES.iconSwapCycle]: {
        category: 'svg',
        indexSummary: 'several icons cycling through one slot to carry a category',
        tags: ['icon', 'cycle', 'swap', 'category', 'any', 'every', 'supports'],
    },
    [SKILL_NAMES.facetMorphShape]: {
        category: 'svg',
        indexSummary: 'a silhouette reshaping into another by moving its points',
        tags: ['morph', 'polygon', 'shape', 'transform', 'metaphor', 'facets', 'becomes'],
    },
    [SKILL_NAMES.splitComparisonCards]: {
        category: 'layout',
        indexSummary: 'two-up comparison, before against after',
        tags: ['comparison', 'versus', 'two-up', 'before-after', 'cards'],
    },
    [SKILL_NAMES.cardGridAssemble]: {
        category: 'layout',
        indexSummary: 'feature cards settling into a grid one slot at a time',
        tags: ['grid', 'cards', 'features', 'capabilities', 'set', 'assemble', 'tour'],
    },
    [SKILL_NAMES.hubAndSpokes]: {
        category: 'layout',
        indexSummary: 'connectors drawn from a center out to satellite nodes',
        tags: ['diagram', 'hub', 'nodes', 'connectors', 'integrations', 'platform', 'structure'],
    },
    [SKILL_NAMES.logoWallAssemble]: {
        category: 'layout',
        indexSummary: 'a tile wall filling in scattered order to read as volume',
        tags: ['logos', 'wall', 'grid', 'customers', 'roster', 'volume', 'many'],
    },
    [SKILL_NAMES.spotlightFocusCard]: {
        category: 'layout',
        indexSummary: 'one item lifting out of a set while the rest recede',
        tags: ['pick', 'recommended', 'plans', 'choice', 'options', 'spotlight', 'compare'],
    },
    [SKILL_NAMES.radialSurround]: {
        category: 'layout',
        indexSummary: 'items arranged evenly around a centre, arriving as a ring',
        tags: ['ring', 'radial', 'orbit', 'surround', 'equal', 'set', 'converge'],
    },
    [SKILL_NAMES.quoteProofPanel]: {
        category: 'layout',
        indexSummary: 'a short quotation with its attribution landing after it',
        tags: ['quote', 'testimonial', 'review', 'proof', 'attribution', 'customer', 'social'],
    },
    [SKILL_NAMES.perspectiveMarquee]: {
        category: 'layout',
        indexSummary: 'tilted rails of cards streaming through the frame with depth',
        tags: ['marquee', 'rail', 'catalogue', 'library', 'portfolio', 'volume', 'depth'],
    },
    [SKILL_NAMES.lowerThirdCallout]: {
        category: 'overlays',
        indexSummary: 'a name and role rising in over the lower frame, then clearing',
        tags: ['lower-third', 'name', 'attribution', 'speaker', 'label', 'overlay', 'source'],
    },
    [SKILL_NAMES.tickerCrawlStrip]: {
        category: 'overlays',
        indexSummary: 'items crawling across a band at constant speed',
        tags: ['ticker', 'crawl', 'feed', 'marquee', 'list', 'news', 'overlay', 'volume'],
    },
    [SKILL_NAMES.cornerBadgeStack]: {
        category: 'overlays',
        indexSummary: 'short qualifying chips stacking into a corner and clearing',
        tags: ['badge', 'chip', 'certification', 'qualifier', 'small-print', 'trust', 'overlay'],
    },
    [SKILL_NAMES.handDrawnAnnotation]: {
        category: 'annotation',
        indexSummary: 'an ink circle, arrow, or bracket drawn onto the frame by hand',
        tags: ['annotation', 'circle', 'arrow', 'lasso', 'bracket', 'hand-drawn', 'point-at'],
    },
    [SKILL_NAMES.calloutWithLeaderLine]: {
        category: 'annotation',
        indexSummary: 'a dot, a leader line, and a short label naming one spot',
        tags: ['callout', 'leader-line', 'label', 'diagram', 'explainer', 'name-this', 'annotate'],
    },
    [SKILL_NAMES.simulatedCursorClick]: {
        category: 'ui',
        indexSummary: 'a cursor crossing a mocked interface and clicking something',
        tags: ['cursor', 'click', 'demo', 'interaction', 'product', 'how-to', 'ui'],
    },
    [SKILL_NAMES.interfaceStateChange]: {
        category: 'ui',
        indexSummary: 'a mocked interface flipping state, with the panel responding',
        tags: ['toggle', 'tabs', 'state', 'panel', 'skeleton', 'product', 'behaviour', 'ui'],
    },
    [SKILL_NAMES.feedScrollDemo]: {
        category: 'ui',
        indexSummary: 'a page or feed scrolling past a viewport and stopping on one item',
        tags: ['scroll', 'feed', 'page', 'viewport', 'in-context', 'stream', 'ui'],
    },
    [SKILL_NAMES.multiDeviceSplay]: {
        category: 'ui',
        indexSummary: 'device frames fanned into an overlapping arrangement',
        tags: ['devices', 'cross-platform', 'everywhere', 'lockup', 'splay', 'launch', 'ui'],
    },
    [SKILL_NAMES.deviceScreenDive]: {
        category: 'ui',
        indexSummary: 'a device in frame whose screen grows to become the frame',
        tags: ['device', 'phone', 'laptop', 'screen', 'dive', 'product', 'context', 'ui'],
    },
    [SKILL_NAMES.ambientFloatLoop]: {
        category: 'ambient',
        indexSummary: 'idle drift keeping the frame alive while content holds',
        tags: ['ambient', 'idle', 'float', 'background', 'loop'],
    },
    [SKILL_NAMES.parallaxDepthDrift]: {
        category: 'ambient',
        indexSummary: 'layers drifting at different rates to build depth',
        tags: ['parallax', 'depth', 'layers', 'background', 'drift'],
    },
    [SKILL_NAMES.atmosphereGradientField]: {
        category: 'ambient',
        indexSummary: 'a soft gradient wash giving the background atmosphere',
        tags: ['background', 'atmosphere', 'gradient', 'ambient', 'depth'],
    },
    [SKILL_NAMES.ambientParticleField]: {
        category: 'ambient',
        indexSummary: 'drifting dust or stars carrying background movement',
        tags: ['background', 'particles', 'ambient', 'depth', 'dust', 'stars'],
    },
    [SKILL_NAMES.meshGradientBackground]: {
        category: 'ambient',
        indexSummary: 'overlapping colour blooms drifting past each other behind the content',
        tags: ['background', 'mesh', 'gradient', 'blooms', 'ambient', 'colour', 'depth'],
    },
    [SKILL_NAMES.grainAndBloom]: {
        category: 'texture',
        indexSummary: 'film grain, bloom, and vignette finishing the frame',
        tags: ['grain', 'texture', 'bloom', 'glow', 'vignette', 'finishing', 'film'],
    },
    [SKILL_NAMES.lightSweepPass]: {
        category: 'texture',
        indexSummary: 'a band of light crossing the frame, re-shading as it goes',
        tags: ['light', 'sweep', 'specular', 'sheen', 'lighting', 'premium', 'still-frame'],
    },
    [SKILL_NAMES.halftoneDotDissolve]: {
        category: 'texture',
        indexSummary: 'one state resolving into another through growing halftone dots',
        tags: ['halftone', 'dots', 'print', 'risograph', 'dissolve', 'texture', 'transition'],
    },
    [SKILL_NAMES.orderedDitherPass]: {
        category: 'texture',
        indexSummary: 'a coarse quantized grid resolving away as fidelity arrives',
        tags: ['dither', 'quantize', 'low-fidelity', 'retro', 'crt', 'resolve', 'signal'],
    },
    [SKILL_NAMES.vignetteFocusPull]: {
        category: 'texture',
        indexSummary: 'a soft vignette closing in on one part of the frame',
        tags: ['vignette', 'isolate', 'attention', 'dim', 'focus', 'spotlight', 'grade'],
    },
    [SKILL_NAMES.cinematicCameraMove]: {
        category: 'camera',
        indexSummary: 'a slow push, pan, or zoom across the whole frame',
        tags: ['camera', 'push', 'pan', 'zoom', 'cinematic'],
    },
    [SKILL_NAMES.pullBackReveal]: {
        category: 'camera',
        indexSummary: 'opening tight on a detail and pulling back to reframe it',
        tags: ['pull-back', 'reveal', 'reframe', 'context', 'zoom-out', 'recontextualize'],
    },
    [SKILL_NAMES.driftAndHold]: {
        category: 'camera',
        indexSummary: 'a continuous slow drift that keeps a settled frame from dying',
        tags: ['drift', 'ambient', 'alive', 'dead-tail', 'hold', 'bed', 'continuous'],
    },
    [SKILL_NAMES.panBetweenStations]: {
        category: 'camera',
        indexSummary: 'a camera travelling between fixed stops on an oversized canvas',
        tags: ['pan', 'stations', 'journey', 'map', 'sequence', 'coexist', 'world'],
    },
    [SKILL_NAMES.momentSweepTransition]: {
        category: 'transitions',
        indexSummary: 'a sweep or wipe marking a change of moment',
        tags: ['transition', 'sweep', 'scene-change', 'moment'],
    },
    [SKILL_NAMES.irisReveal]: {
        category: 'transitions',
        indexSummary: 'a circle opening from a point to swap the frame',
        tags: ['iris', 'circle', 'clip-path', 'before-after', 'reveal', 'transition'],
    },
    [SKILL_NAMES.slatWipeReveal]: {
        category: 'transitions',
        indexSummary: 'staggered slats clearing the frame like a blind opening',
        tags: ['slats', 'wipe', 'blind', 'mechanical', 'reveal', 'transition'],
    },
    [SKILL_NAMES.sharedAxisSlide]: {
        category: 'transitions',
        indexSummary: 'one state leaving as the next arrives along the same axis',
        tags: ['steps', 'sequence', 'slide', 'next', 'walkthrough', 'transition', 'phase'],
    },
    [SKILL_NAMES.zoomThroughTransition]: {
        category: 'transitions',
        indexSummary: 'flying into an aperture until it fills and passes the camera',
        tags: ['punch-through', 'zoom', 'aperture', 'inside', 'travel', 'transition'],
    },
    [SKILL_NAMES.velocityMatchedCut]: {
        category: 'handoff',
        indexSummary: 'a cut whose motion continues across it',
        tags: ['transition', 'cut', 'velocity', 'continuous-motion'],
    },
    [SKILL_NAMES.whipPanCut]: {
        category: 'handoff',
        indexSummary: 'a fast sideways smear hiding a cut inside the motion',
        tags: ['whip-pan', 'blur', 'energy', 'cut', 'momentum', 'subject-change'],
    },
    [SKILL_NAMES.shapeMatchCut]: {
        category: 'handoff',
        indexSummary: 'a cut hidden on a shape both states share',
        tags: ['match-cut', 'shared-shape', 'continuity', 'persist', 'cut', 'same-thing'],
    },
    [SKILL_NAMES.elementMorphHandoff]: {
        category: 'handoff',
        indexSummary: 'one element becoming the next instead of being replaced',
        tags: ['transition', 'morph', 'handoff', 'continuous'],
    },
    [SKILL_NAMES.morphChainSpine]: {
        category: 'handoff',
        indexSummary: 'a single shape carried through every phase of the graphic',
        tags: ['transition', 'morph', 'chain', 'continuous', 'flood', 'multi-state'],
    },
    [SKILL_NAMES.springSettle]: {
        category: 'craft',
        indexSummary: 'an object overshooting and settling, with something reacting after',
        tags: ['spring', 'overshoot', 'bounce', 'physical', 'confirmation', 'badge', 'check'],
    },
    [SKILL_NAMES.motionBlurStreak]: {
        category: 'craft',
        indexSummary: 'a smear along the direction of travel at peak velocity',
        tags: ['motion-blur', 'speed', 'smear', 'weight', 'fast', 'streak'],
    },
    [SKILL_NAMES.weightedExit]: {
        category: 'craft',
        indexSummary: 'an element leaving the frame instead of fading to nothing',
        tags: ['exit', 'leave', 'physical', 'clear', 'phase', 'discard', 'make-way'],
    },
    [SKILL_NAMES.beatSyncAccent]: {
        category: 'craft',
        indexSummary: 'every accent landing on one regular interval, so the piece reads as scored',
        tags: ['beat', 'rhythm', 'timing', 'grid', 'music', 'cadence', 'structure'],
    },
    [SKILL_NAMES.echoTrailGhosts]: {
        category: 'craft',
        indexSummary: 'fading copies trailing a moving element, for speed or a path',
        tags: ['trail', 'ghosts', 'onion-skin', 'speed', 'path', 'repetition', 'echo'],
    },
    [SKILL_NAMES.stopMotionCadence]: {
        category: 'craft',
        indexSummary: 'motion advancing in held steps instead of interpolating',
        tags: ['stop-motion', 'steps', 'handmade', 'cut-paper', 'flipbook', 'boil', 'cadence'],
    },
    [SKILL_NAMES.motionPolish]: {
        category: 'craft',
        indexSummary: 'easing, overlap, and follow-through that read as designed',
        tags: ['easing', 'overlap', 'stagger', 'timing', 'choreography', 'polish'],
    },
    [SKILL_NAMES.sustainedSceneReveal]: {
        category: 'craft',
        indexSummary: 'pacing a long scene so nothing stalls or dies at the tail',
        tags: ['pacing', 'sequence', 'voice-sync', 'hold', 'rhythm', 'choreography'],
    },
};
